import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, Check, ArrowRight } from "lucide-react";
import axios from "axios";
import { useLang } from "@/lib/lang";
import { API } from "./constants";

/**
 * Signup modal for partner / driver / fleet applications. Renders the fields
 * defined in `option.fields` and POSTs to `option.endpoint`.
 */
export default function JoinModal({ option, onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const update = (name) => (e) => setForm((s) => ({ ...s, [name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}${option.endpoint}`, form);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t("join_error_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = option.icon;

  return (
    <motion.div
      className="dt-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid={`join-modal-${option.key}`}
    >
      <motion.div
        className="dt-modal__box dt-modal__box--sm"
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="dt-modal__close"
          onClick={onClose}
          aria-label={t("booking_close_label")}
          data-testid={`join-close-${option.key}`}
        >
          <X size={20} />
        </button>

        {!success ? (
          <>
            <div className="dt-modal__header">
              <div className="dt-modal__icon"><Icon size={22} /></div>
              <p className="dt-section__eyebrow">{t("join_eyebrow")}</p>
              <h3 className="dt-modal__title">{t(`landing_join_${option.key}_title`)}</h3>
              <p className="dt-modal__lead">{t(`landing_join_${option.key}_tagline`)}</p>
            </div>
            <form className="dt-form" onSubmit={submit}>
              {option.fields.map((f) => (
                <label key={f.name} className="dt-form__field">
                  <span>
                    {t(f.labelKey)}
                    {f.required && " *"}
                  </span>
                  {f.type === "select" ? (
                    <select
                      required={f.required}
                      value={form[f.name] || ""}
                      onChange={update(f.name)}
                      data-testid={`join-field-${option.key}-${f.name}`}
                    >
                      <option value="">{t("join_choose_placeholder")}</option>
                      {f.options.map((o) => (
                        <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      required={f.required}
                      type={f.type || "text"}
                      minLength={f.minLength}
                      value={form[f.name] || ""}
                      onChange={update(f.name)}
                      data-testid={`join-field-${option.key}-${f.name}`}
                    />
                  )}
                </label>
              ))}
              {error && (
                <div className="dt-form__error" data-testid={`join-error-${option.key}`}>
                  {error}
                </div>
              )}
              <div className="dt-modal__footer">
                <button type="button" className="dt-btn dt-btn--ghost" onClick={onClose}>
                  {t("join_action_cancel")}
                </button>
                <button
                  type="submit"
                  className="dt-btn dt-btn--primary"
                  disabled={submitting}
                  data-testid={`join-submit-${option.key}`}
                >
                  {submitting ? t("join_action_sending") : (<>{t("join_action_submit")} <ArrowRight size={14} /></>)}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="dt-success" data-testid={`join-success-${option.key}`}>
            <div className="dt-success__badge"><Check size={32} /></div>
            <h3 className="dt-modal__title">{t("join_success_title")}</h3>
            <p className="dt-success__lead">{t("join_success_body")}</p>
            <div className="dt-modal__footer">
              <button
                type="button"
                className="dt-btn dt-btn--primary"
                onClick={onClose}
                data-testid={`join-done-${option.key}`}
              >
                {t("join_action_done")}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
